import { PlusIcon } from '@heroicons/react/24/outline';
import { LoadingBlockFullPage } from '../../../components/loading/loading-block-full-page.tsx';
import { Button } from '../../../components/ui/button.tsx';
import { DrawerContainer, DrawerContent, DrawerHeader } from '../../../components/ui/drawer.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import { api } from '../../../utils/api.ts';
import { getEventMetadata } from '../../../utils/event-utils.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { DialogStateManager } from '../../dialog/dialog-provider.tsx';
import { TraskCreateDialog } from '../../tasks/components/task-create-dialog.tsx';
import { EventDetailsSchedule } from './event-details-schedule.tsx';
import { EventEditDialog } from './event-edit-dialog.tsx';
import { EventOptionsDropdown } from './event-options-dropdown.tsx';

export type EventDetailsProps = {
    date: string;
    eventId: string;
    className?: string;
    onBack?: () => void;
    onClose?: () => void;
    onUpdated?: () => void;
};

export const EventDetails = ({ date, eventId, className, onBack, onClose, onUpdated }: EventDetailsProps) => {
    const { t } = useLocale('events');
    const sendEvent = useAnalytics();
    const { userId } = useAppConfig();

    // Use the direct getEventById endpoint
    const { data: eventDetails, isLoading } = api.platoUser.query.events.getEventById.useQuery(
        {
            eventId,
        },
        {
            enabled: !!eventId,
        },
    );

    const handleEdit = () => {
        if (!eventDetails) {
            return;
        }
        void DialogStateManager.show(EventEditDialog, {
            eventId,
            eventDetails,
            occurrenceDate: date,
            onUpdated,
        });
    };

    if (isLoading || !eventDetails) {
        return <LoadingBlockFullPage className="mt-64" />;
    }

    // Determine if the current user is the owner of the event
    const isOwner = eventDetails.owner_id === userId;

    return (
        <DrawerContainer className={className}>
            <DrawerHeader
                onBack={onBack}
                onClose={onClose}
                actions={
                    isOwner && (
                        <>
                            <Button
                                variant="subtle"
                                icon={PlusIcon}
                                onClick={() => {
                                    void DialogStateManager.show(TraskCreateDialog, {
                                        links: eventDetails.customer?.customerId
                                            ? [
                                                  {
                                                      modelType: SearchEntity.Customer,
                                                      modelId: eventDetails.customer.customerId,
                                                      title: eventDetails.customer.customerName ?? null,
                                                  },
                                              ]
                                            : undefined,
                                        source: 'event_details',
                                    });
                                }}
                            >
                                {t('details__new_task', { ns: 'events' })}
                            </Button>
                            <EventOptionsDropdown
                                date={date}
                                event={eventDetails}
                                onEdit={handleEdit}
                                onDelete={() => {
                                    onUpdated?.();
                                    onClose?.();
                                    sendEvent('event_deleted', getEventMetadata(eventDetails));
                                }}
                            />
                        </>
                    )
                }
                title={t('details__title')}
            />
            <DrawerContent>
                <EventDetailsSchedule eventDetails={eventDetails} occurrenceDate={date} />
            </DrawerContent>
        </DrawerContainer>
    );
};
